import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { ExpectedDeliveryItem, OwnerType } from "../../../_models/expected-delivery.interface";
import { ArticleTemplate } from "../../../_models";
import {
  ArticleTemplatePickerComponent,
  PickerProjectRef,
  PickerCustomerRef,
  PickerWarehouseRef,
} from "../../_shared/article-template-picker";

/**
 * Daten die beim Speichern emittiert werden
 */
export interface ItemDialogSaveData {
  id?: number;
  article_template_id: number;
  quantity_ordered: number;
  owner: OwnerType;
  _destroy?: boolean;
}

@Component({
  selector: "app-expected-delivery-item-dialog",
  standalone: true,
  imports: [FormsModule, ArticleTemplatePickerComponent],
  templateUrl: "./expected-delivery-item-dialog.component.html",
})
export class ExpectedDeliveryItemDialogComponent implements OnInit {
  /** Modus: 'create' für neue Position, 'edit' für bestehende */
  @Input() mode: "create" | "edit" = "create";

  /** Das zu bearbeitende Item (nur bei mode='edit') */
  @Input() item?: ExpectedDeliveryItem;

  /** Projekt für Artikelvorlagen-Filter (optional) */
  @Input() project: PickerProjectRef | null = null;

  /** Kunde für Artikelvorlagen-Filter (optional) */
  @Input() customer: PickerCustomerRef | null = null;

  /** Quell-Lager für Filter bei internen Lieferungen (optional) */
  @Input() sourceWarehouse: PickerWarehouseRef | null = null;

  /** Artikelvorlagen-IDs die im Quell-Lager vorhanden sind (für Lager-Filter) */
  @Input() warehouseArticleTemplateIds: number[] | null = null;

  /** Optionale externe Artikelvorlagen-Liste (vermeidet erneutes Laden) */
  @Input() articleTemplates: ArticleTemplate[] | null = null;

  /** Emittiert die gespeicherten Daten */
  @Output() saved = new EventEmitter<ItemDialogSaveData>();

  /** Emittiert neu geladene Templates (nach Reload im Picker) */
  @Output() templatesReloaded = new EventEmitter<ArticleTemplate[]>();

  /** Emittiert die Item-ID zum Löschen */
  @Output() deleted = new EventEmitter<number>();

  /** Emittiert wenn Dialog geschlossen wird */
  @Output() closed = new EventEmitter<void>();

  // Formular-Daten
  selectedTemplate = signal<ArticleTemplate | null>(null);
  quantityOrdered = signal<number>(1);
  owner = signal<OwnerType>("BENTOMAX");

  // Template-Picker Modal
  showTemplatePicker = signal<boolean>(false);

  // Computed: Minimum-Anzahl (bei edit: max(1, quantity_received))
  minQuantity = computed(() => {
    if (this.mode === "create") return 1;
    return Math.max(1, this.item?.quantity_received || 0);
  });

  // Computed: Kann gelöscht werden (nur wenn nichts angekommen)
  canDelete = computed(() => {
    if (this.mode === "create") return false;
    return (this.item?.quantity_received || 0) === 0;
  });

  // Computed: Kann Eigentümer geändert werden (bei create immer, bei edit nur wenn nichts angekommen)
  canEditOwner = computed(() => {
    if (this.mode === "create") return true;
    return (this.item?.quantity_received || 0) === 0;
  });

  // Computed: Formulardaten valide
  isValid = computed(() => {
    if (this.mode === "create") {
      return this.selectedTemplate() !== null && this.quantityOrdered() >= 1;
    }
    return this.quantityOrdered() >= this.minQuantity();
  });

  // Info-Anzeige für Edit-Modus
  get quantityReceived(): number {
    return this.item?.quantity_received || 0;
  }

  get quantityOpen(): number {
    return this.quantityOrdered() - this.quantityReceived;
  }

  ngOnInit(): void {
    if (this.mode === "edit" && this.item) {
      // Werte vom Item übernehmen
      this.quantityOrdered.set(this.item.quantity_ordered);
      this.owner.set(this.item.owner);

      // Template-Info setzen (nur für Anzeige)
      if (this.item.article_template_id) {
        this.selectedTemplate.set({
          id: this.item.article_template_id,
          description: this.item.article_template_description || "",
          part_nr: this.item.article_template_part_nr || undefined,
          sn: this.item.article_template_sn || false,
          vendor: this.item.article_template_vendor || undefined,
        } as ArticleTemplate);
      }
    } else {
      // Default-Owner basierend auf Kunde
      this.owner.set(this.customer?.id ? "CUSTOMER" : "BENTOMAX");
    }
  }

  /**
   * Öffnet den Artikelvorlagen-Picker (nur bei create)
   */
  openTemplatePicker(): void {
    if (this.mode === "create") {
      this.showTemplatePicker.set(true);
    }
  }

  /**
   * Schließt den Artikelvorlagen-Picker
   */
  closeTemplatePicker(): void {
    this.showTemplatePicker.set(false);
  }

  /**
   * Wird aufgerufen wenn eine Vorlage ausgewählt wird
   */
  onTemplateSelected(template: ArticleTemplate): void {
    this.selectedTemplate.set(template);
    this.showTemplatePicker.set(false);
  }

  /**
   * Leitet das Reload-Event vom Picker weiter
   */
  onPickerTemplatesReloaded(templates: ArticleTemplate[]): void {
    this.templatesReloaded.emit(templates);
  }

  /**
   * Erhöht die Anzahl
   */
  incrementQuantity(): void {
    this.quantityOrdered.update((q) => q + 1);
  }

  /**
   * Verringert die Anzahl (nicht unter Minimum)
   */
  decrementQuantity(): void {
    const min = this.minQuantity();
    this.quantityOrdered.update((q) => Math.max(min, q - 1));
  }

  /**
   * Setzt die Anzahl (mit Validierung)
   */
  setQuantity(value: number): void {
    const min = this.minQuantity();
    this.quantityOrdered.set(Math.max(min, value));
  }

  /**
   * Speichert die Änderungen
   */
  save(): void {
    if (!this.isValid()) return;

    const data: ItemDialogSaveData = {
      article_template_id: this.selectedTemplate()!.id,
      quantity_ordered: this.quantityOrdered(),
      owner: this.owner(),
    };

    if (this.mode === "edit" && this.item?.id) {
      data.id = this.item.id;
    }

    this.saved.emit(data);
  }

  /**
   * Löscht die Position
   */
  delete(): void {
    if (!this.canDelete() || !this.item?.id) return;
    this.deleted.emit(this.item.id);
  }

  /**
   * Schließt den Dialog
   */
  close(): void {
    this.closed.emit();
  }

  /**
   * Gibt das Owner-Label zurück
   */
  getOwnerLabel(owner: OwnerType): string {
    return owner === "BENTOMAX" ? "Bentomax" : "Kunde";
  }
}
